import React, { useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  Button,
  Checkbox,
  Dialog,
  FormLabel,
  IconButton,
  TextField,
  Tooltip,
} from '@material-ui/core'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import DmsApiService from 'Services/DMS/dms-api.service'
import AlertToastr from 'utils/alert'
import useTranslation from 'Hooks/useTranslation'

const DEFAULT_RATE_LIMITS = {
  rateLimitPerMinute: 100,
  rateLimitPerHour: 1000,
  rateLimitPerDay: 10000,
}

const datePickerPopperContainer = ({ children }) =>
  typeof document !== 'undefined'
    ? ReactDOM.createPortal(children, document.body)
    : children

const datePickerProps = {
  popperContainer: datePickerPopperContainer,
  popperClassName: 'custom-datepicker-popper',
  popperPlacement: 'bottom-start',
  popperModifiers: {
    preventOverflow: {
      enabled: true,
      escapeWithReference: false,
      boundariesElement: 'viewport',
    },
  },
}

const showApiError = (error) => {
  const errData = error?.response?.data
  if (!errData) {
    AlertToastr.showErrorAlert('An unexpected error occurred.')
    return
  }
  if (Array.isArray(errData.errors) && errData.errors.length > 0) {
    errData.errors.forEach((e) => {
      AlertToastr.showErrorAlert(e.ErrorMessage || e.errorMessage || e)
    })
    return
  }
  if (typeof errData.Message === 'string') {
    AlertToastr.showErrorAlert(errData.Message)
    return
  }
  if (typeof errData.message === 'string') {
    AlertToastr.showErrorAlert(errData.message)
    return
  }
  AlertToastr.showErrorAlert('An unexpected error occurred.')
}

const ApiKeyModal = ({
  isOpen,
  isOpenChangeHandler,
  apiKeyData,
  setApiKeyData,
  fetchApiKeys,
  canChangeRateLimits,
  requestConfig,
}) => {
  const translation = useTranslation()
  const isEdit = Boolean(apiKeyData?.id)

  const [name, setName] = useState('')
  const [expiresAt, setExpiresAt] = useState(null)
  const [isActive, setIsActive] = useState(true)
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(DEFAULT_RATE_LIMITS.rateLimitPerMinute)
  const [rateLimitPerHour, setRateLimitPerHour] = useState(DEFAULT_RATE_LIMITS.rateLimitPerHour)
  const [rateLimitPerDay, setRateLimitPerDay] = useState(DEFAULT_RATE_LIMITS.rateLimitPerDay)
  const [createdKey, setCreatedKey] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    if (apiKeyData) {
      setName(apiKeyData.name || '')
      setExpiresAt(apiKeyData.expiresAt ? moment.utc(apiKeyData.expiresAt).local().toDate() : null)
      setIsActive(apiKeyData.isActive !== false)
      setRateLimitPerMinute(apiKeyData.rateLimitPerMinute ?? DEFAULT_RATE_LIMITS.rateLimitPerMinute)
      setRateLimitPerHour(apiKeyData.rateLimitPerHour ?? DEFAULT_RATE_LIMITS.rateLimitPerHour)
      setRateLimitPerDay(apiKeyData.rateLimitPerDay ?? DEFAULT_RATE_LIMITS.rateLimitPerDay)
      setCreatedKey(null)
      return
    }

    setName('')
    setExpiresAt(null)
    setIsActive(true)
    setRateLimitPerMinute(DEFAULT_RATE_LIMITS.rateLimitPerMinute)
    setRateLimitPerHour(DEFAULT_RATE_LIMITS.rateLimitPerHour)
    setRateLimitPerDay(DEFAULT_RATE_LIMITS.rateLimitPerDay)
    setCreatedKey(null)
  }, [isOpen, apiKeyData])

  const canSubmit = useMemo(() => name.trim().length > 0 && name.trim().length <= 128, [name])

  const onClose = () => {
    setApiKeyData?.(null)
    setCreatedKey(null)
    isOpenChangeHandler(false)
  }

  const copyKey = (key) => {
    if (!key) return
    navigator.clipboard.writeText(key)
    AlertToastr.showAlert(translation.settingsApiKeysCopied)
  }

  const onSubmit = async () => {
    if (!canSubmit || saving) return
    setSaving(true)

    try {
      if (isEdit) {
        const payload = {
          id: apiKeyData.id,
          name: name.trim(),
          expiresAt: expiresAt ? moment(expiresAt).utc().toISOString() : null,
          rateLimitPerMinute: Number(rateLimitPerMinute),
          rateLimitPerHour: Number(rateLimitPerHour),
          rateLimitPerDay: Number(rateLimitPerDay),
          isActive,
        }
        const response = await DmsApiService.updateApiKey(payload, requestConfig)
        if (response?.data?.isSuccessful !== false) {
          AlertToastr.showAlert(
            response?.data?.message || translation.settingsApiKeysUpdatedSuccess
          )
        }
        onClose()
        fetchApiKeys?.()
      } else {
        const payload = {
          name: name.trim(),
        }
        if (expiresAt) {
          payload.expiresAt = moment(expiresAt).utc().toISOString()
        }
        const response = await DmsApiService.createApiKey(payload, requestConfig)
        const created = response?.data
        AlertToastr.showAlert(translation.settingsApiKeysCreatedSuccess)
        if (created?.key) {
          setCreatedKey(created)
        } else {
          onClose()
        }
        fetchApiKeys?.()
      }
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  const modalTitle = createdKey
    ? translation.settingsApiKeysCreatedSuccess
    : isEdit
      ? translation.settingsApiKeysEditTitle
      : translation.settingsApiKeysCreateTitle

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="respModal scrollContentModal calendarModal autoHeight datepicker-modal"
    >
      <div className="formContentHolder">
        <div className="modalTitleHolder">
          <h1 className="modalTitle text-center">{modalTitle}</h1>
        </div>

        {createdKey ? (
          <>
            <div className="formContent">
              <div className="formContentBlock modalFieldLabelSettings">
                <FormLabel component="legend" className="mb5 modalFieldLabel">
                  {translation.settingsApiKeysNameLabel}
                </FormLabel>
                <TextField
                  variant="outlined"
                  margin="normal"
                  value={createdKey.name || ''}
                  fullWidth
                  disabled
                />
                <FormLabel component="legend" className="mb5 modalFieldLabel">
                  {translation.settingsApiKeysKeyHeader}
                </FormLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    value={createdKey.key || ''}
                    fullWidth
                    disabled
                    style={{ marginTop: 0 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => copyKey(createdKey.key)}
                    id="settingsApiKeyCopyCreatedBtn"
                  >
                    {translation.settingsApiKeysCopyLabel}
                  </Button>
                </div>
              </div>
            </div>
            <div className="btnHolder end fixedWidthBtn">
              <Button
                onClick={onClose}
                variant="contained"
                color="primary"
                id="settingsApiKeyCreatedDoneBtn"
              >
                OK
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="formContent">
              <div className="formContentBlock modalFieldLabelSettings">
                <FormLabel component="legend" className="mb5 modalFieldLabel">
                  {translation.settingsApiKeysNameLabel} *
                </FormLabel>
                <TextField
                  variant="outlined"
                  margin="normal"
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 128))}
                  fullWidth
                  required
                  inputProps={{
                    autoComplete: 'off',
                    maxLength: 128,
                  }}
                  id="settingsApiKeyNameInput"
                />

                <FormLabel component="legend" className="mb5 modalFieldLabel">
                  {translation.settingsApiKeysExpiresLabel}
                </FormLabel>
                <div className="datePickerWithLabel apiKeyDatePicker" style={{ marginBottom: 16, position: 'relative' }}>
                  <DatePicker
                    selected={expiresAt}
                    onChange={(date) => setExpiresAt(date)}
                    minDate={new Date()}
                    className="formControlDatePicker"
                    dateFormat="MM/dd/yyyy"
                    placeholderText={translation.settingsApiKeysExpiresPlaceholder}
                    dropdownMode="select"
                    id="settingsApiKeyExpiresPicker"
                    {...datePickerProps}
                  />
                  {expiresAt && (
                    <IconButton
                      className="iconBtnSm apiKeyDatePickerClearBtn"
                      size="small"
                      onClick={() => setExpiresAt(null)}
                      aria-label="Clear expiration date"
                      id="settingsApiKeyExpiresClearBtn"
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  )}
                </div>

                {isEdit && (
                  <div
                    className="formCheckBoxHolder"
                    style={{ display: 'flex', alignItems: 'center', marginTop: 4, marginBottom: 8 }}
                  >
                    <Checkbox
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      size="small"
                      color="default"
                      className={isActive ? 'checkedCheckboxLabel' : ''}
                      id="settingsApiKeyActiveCheckbox"
                      style={{ padding: 0, marginRight: 8 }}
                    />
                    <span className={isActive ? 'checkedCheckboxLabel' : 'сheckboxLabel'}>
                      {translation.settingsApiKeysActiveLabel}
                    </span>
                  </div>
                )}

                {isEdit && (
                  <Tooltip
                    title={
                      canChangeRateLimits
                        ? ''
                        : 'Only Superuser, Master, Root or Admin can change rate limits'
                    }
                  >
                    <div>
                      <FormLabel component="legend" className="mb5 modalFieldLabel">
                        {translation.settingsApiKeysRateLimitsHeader}
                      </FormLabel>
                      <TextField
                        variant="outlined"
                        margin="dense"
                        label={translation.settingsApiKeysRateLimitPerMinuteLabel}
                        type="number"
                        value={rateLimitPerMinute}
                        onChange={(e) => setRateLimitPerMinute(e.target.value)}
                        disabled={!canChangeRateLimits}
                        fullWidth
                        inputProps={{ min: 1 }}
                        id="settingsApiKeyRateLimitPerMinute"
                      />
                      <TextField
                        variant="outlined"
                        margin="dense"
                        label={translation.settingsApiKeysRateLimitPerHourLabel}
                        type="number"
                        value={rateLimitPerHour}
                        onChange={(e) => setRateLimitPerHour(e.target.value)}
                        disabled={!canChangeRateLimits}
                        fullWidth
                        inputProps={{ min: 1 }}
                        id="settingsApiKeyRateLimitPerHour"
                      />
                      <TextField
                        variant="outlined"
                        margin="dense"
                        label={translation.settingsApiKeysRateLimitPerDayLabel}
                        type="number"
                        value={rateLimitPerDay}
                        onChange={(e) => setRateLimitPerDay(e.target.value)}
                        disabled={!canChangeRateLimits}
                        fullWidth
                        inputProps={{ min: 1 }}
                        id="settingsApiKeyRateLimitPerDay"
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="btnHolder end fixedWidthBtn">
              <Button onClick={onClose} variant="outlined" id="settingsApiKeyCancelBtn">
                {translation.cancelButtonLabel}
              </Button>
              <Button
                onClick={onSubmit}
                variant="contained"
                color="primary"
                disabled={!canSubmit || saving}
                id="settingsApiKeySaveBtn"
              >
                {isEdit ? translation.saveButtonLabel : translation.createButtonLabel}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}

export default ApiKeyModal
